import { getAuthToken, getLocalApiBaseUrl } from "./deploymentConfig";

const buildUrl = (path, query) => {
  const base = getLocalApiBaseUrl();
  const cleanPath = String(path || "").startsWith("/") ? path : `/${path}`;
  const origin = typeof window !== "undefined" ? window.location.origin : "http://localhost";
  const url = base.startsWith("http")
    ? new URL(`${base}${cleanPath}`)
    : new URL(`${base.replace(/\/+$/, "")}${cleanPath}`, origin);
  Object.entries(query || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    url.searchParams.set(key, String(value));
  });
  return url.toString();
};

const parseResponse = async (response) => {
  const text = await response.text();
  const contentType = response.headers.get("content-type") || "";
  const looksLikeHtml = /^\s*</.test(text || "");
  let data = null;

  if (text) {
    if (contentType.includes("application/json") || !looksLikeHtml) {
      try {
        data = JSON.parse(text);
      } catch (err) {
        if (looksLikeHtml) {
          data = null;
        } else {
          throw new Error(`Local API returned an invalid response (${response.status}).`);
        }
      }
    }
  }

  if (looksLikeHtml) {
    const message = response.status >= 500
      ? "ClearHealth Local API is starting or unavailable. Please wait a moment, then refresh."
      : "ClearHealth Local API returned the web app instead of API data. Check that the local server proxy is running correctly.";
    throw new Error(message);
  }

  if (!response.ok) {
    const message = data?.message || data?.error || `Local API request failed (${response.status})`;
    throw new Error(Array.isArray(message) ? message.join(", ") : message);
  }
  return data;
};

const request = async (method, path, body, options = {}) => {
  const token = getAuthToken();
  const headers = {
    Accept: "application/json",
    ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const response = await fetch(buildUrl(path, options.query), {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  return parseResponse(response);
};

export const localApiClient = {
  get: (path, query, options = {}) => request("GET", path, undefined, { ...options, query }),
  post: (path, body, options = {}) => request("POST", path, body, options),
  patch: (path, body, options = {}) => request("PATCH", path, body, options),
  delete: (path, options = {}) => request("DELETE", path, undefined, options),
};

export default localApiClient;
